<template>
  <div>
    <v-card class="ma-3 px-3">
      <v-card-title class="headline pl-0">Pay UTA</v-card-title>

      <div v-if="!showConfirmView">
        <!-- 3 sections side by side -->
        <v-layout row wrap align-start class="sections-row">
          <!-- SECTION 1: Payer Information -->
          <v-flex xs12 md4 class="section-col">
            <div class="cp-section">
              <div class="section-label mb-3">
                <v-icon small color="success" class="mr-1">mdi-account</v-icon>
                Payer Information
              </div>

              <!-- Lookup banner inside payer section (admin only) -->
              <div
                v-if="!selectedPersonLoaded && allowAdminFeatures"
                class="lookup-banner mb-3"
                @click="accountLookupDialog = true"
              >
                <v-icon color="success" size="20" class="mr-2"
                  >mdi-account-search</v-icon
                >
                <div style="flex: 1;">
                  <div
                    class="font-weight-medium body-2"
                    style="color: #2e7d32;"
                  >
                    Have an existing account?
                  </div>
                  <div class="caption grey--text">
                    Lookup to auto-fill &amp; load saved cards
                  </div>
                </div>
                <v-btn
                  small
                  color="success"
                  @click.stop="accountLookupDialog = true"
                  class="ml-2"
                >
                  Lookup
                </v-btn>
              </div>

              <!-- Account loaded chip inside payer section (admin only) -->
              <div
                v-if="selectedPersonLoaded && allowAdminFeatures"
                class="account-loaded-chip mb-3"
              >
                <v-progress-circular
                  v-if="loadingAccountDetails"
                  indeterminate
                  color="success"
                  size="18"
                  width="2"
                  class="mr-2"
                ></v-progress-circular>
                <v-icon v-else color="success" small class="mr-2"
                  >mdi-check-circle</v-icon
                >
                <div style="flex: 1; min-width: 0;">
                  <div
                    class="font-weight-medium body-2"
                    style="color: #2e7d32; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                  >
                    {{ name }}
                  </div>
                  <div class="caption grey--text">
                    {{
                      loadingAccountDetails
                        ? "Loading account details…"
                        : selectedAccountId || "No UTA account ID"
                    }}
                  </div>
                </div>
                <v-btn
                  flat
                  icon
                  small
                  color="grey"
                  @click="accountLookupDialog = true"
                  title="Change account"
                  ><v-icon small>mdi-pencil</v-icon></v-btn
                >
                <v-btn
                  flat
                  icon
                  small
                  color="grey"
                  @click="clearAccount()"
                  title="Clear account"
                  ><v-icon small>mdi-close</v-icon></v-btn
                >
              </div>

              <v-text-field
                box
                v-model="name"
                label="Full Name *"
                ref="name"
                :rules="nameRules"
                class="mb-1"
              ></v-text-field>

              <v-layout row>
                <v-flex xs6 class="pr-2">
                  <v-text-field
                    box
                    v-model="phoneNumber"
                    label="Phone"
                    ref="phoneNumber"
                    :rules="phoneRules"
                    class="mb-1"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 class="pl-2">
                  <v-text-field
                    box
                    v-model="emailAddress"
                    label="Email Receipt To"
                    ref="emailAddress"
                    :rules="emailRules"
                    class="mb-1"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-text-field
                box
                v-model="address"
                label="Address"
                hide-details
              ></v-text-field>
            </div>
          </v-flex>

          <!-- SECTION 2: Payment Details -->
          <v-flex xs12 md4 class="section-col">
            <div class="cp-section">
              <div class="section-label mb-3">
                <v-icon small color="success" class="mr-1"
                  >mdi-currency-usd</v-icon
                >
                Payment Details
              </div>

              <!-- Department (admin: selectable; global user: fixed to Donation, hidden) -->
              <v-autocomplete
                v-if="allowAdminFeatures"
                box
                :items="
                  departments.map((d) => ({
                    text: d.name,
                    value: d.departmentId,
                  }))
                "
                v-model="selectedDepartmentId"
                label="Department *"
                ref="department"
                :rules="departmentRules"
                :loading="loadingDepartments"
                clearable
                class="mb-3"
              ></v-autocomplete>

              <!-- Note -->
              <v-text-field
                box
                v-model="note"
                label="Note *"
                ref="note"
                :rules="noteRules"
                class="mb-0"
              ></v-text-field>

              <v-divider class="mb-3"></v-divider>

              <!-- Amount -->
              <v-text-field
                box
                v-model="amount"
                label="Amount *"
                type="number"
                prefix="$"
                ref="amount"
                @input="generateSchedules()"
                :rules="amountRules"
                class="mb-2"
              ></v-text-field>

              <!-- Payment type: One-time vs Schedule -->
              <div class="payment-type-label mb-2">Payment Type</div>
              <div class="mode-toggle-group mb-3">
                <button
                  type="button"
                  class="mode-toggle-btn"
                  :class="{
                    'mode-toggle-btn--active': paymentMode === 'single',
                  }"
                  @click="
                    paymentMode = 'single';
                    schedules = [];
                    generateSchedules();
                  "
                >
                  <v-icon small class="mr-1">mdi-cash-fast</v-icon>One-time
                </button>
                <button
                  type="button"
                  class="mode-toggle-btn"
                  :class="{
                    'mode-toggle-btn--active': paymentMode === 'schedule',
                  }"
                  @click="
                    paymentMode = 'schedule';
                    generateSchedules();
                  "
                >
                  <v-icon small class="mr-1">mdi-calendar-clock</v-icon>Schedule
                </button>
              </div>

              <!-- Schedule config + rows (when schedule selected) -->
              <div v-if="paymentMode === 'schedule'" class="schedule-config">
                <v-layout row wrap>
                  <v-flex xs12>
                    <v-text-field
                      type="number"
                      label="# of Payments"
                      ref="numberOfPayments"
                      v-model="numberOfPayments"
                      @input="generateSchedules()"
                      :rules="numberOfPaymentsRules"
                      hide-details
                      class="mb-3"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 v-if="!showCalendarDays" class="mb-3">
                    <v-layout row wrap>
                      <v-flex xs6 class="pr-2">
                        <div class="payment-type-label mb-1 mt-0">
                          Interval
                        </div>
                        <div class="mini-toggle-group">
                          <button
                            type="button"
                            class="mini-toggle-btn"
                            :class="{
                              'mini-toggle-btn--active':
                                scheduleMode === 'month',
                            }"
                            @click="
                              scheduleMode = 'month';
                              generateSchedules();
                            "
                          >
                            Month
                          </button>
                          <button
                            type="button"
                            class="mini-toggle-btn"
                            :class="{
                              'mini-toggle-btn--active':
                                scheduleMode === 'week',
                            }"
                            @click="
                              scheduleMode = 'week';
                              generateSchedules();
                            "
                          >
                            Week
                          </button>
                        </div>
                      </v-flex>
                      <v-flex xs6 class="pl-2">
                        <v-text-field
                          type="number"
                          label="Every"
                          ref="scheduleEvery"
                          v-model="scheduleEvery"
                          @input="generateSchedules()"
                          :rules="scheduleEveryRules"
                          :suffix="scheduleIntervalSuffix"
                          min="1"
                          hide-details
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>

                <!-- Start date -->
                <v-menu
                  v-model="datePickerMenu"
                  :close-on-content-click="true"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="startDate"
                      label="Start Date"
                      ref="startDate"
                      :rules="startDateRules"
                      prepend-icon="mdi-calendar"
                      readonly
                      class="mb-2"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    :min="startDateMin"
                    @change="startDateChanged()"
                  ></v-date-picker>
                </v-menu>

                <!-- By day of month -->
                <v-switch
                  v-model="showCalendarDays"
                  color="success"
                  label="By Day Of Month"
                  @change="generateSchedules"
                  hide-details
                  class="mt-3 mb-3"
                ></v-switch>

                <!-- Calendar day selector -->
                <v-layout row align-center v-if="showCalendarDays" class="mb-3">
                  <div class="mini-toggle-group mr-3">
                    <button
                      type="button"
                      class="mini-toggle-btn"
                      :class="{
                        'mini-toggle-btn--active': calendarMode === 'hebrew',
                      }"
                      @click="
                        calendarMode = 'hebrew';
                        calendarModeChanged();
                      "
                    >
                      Hebrew
                    </button>
                    <button
                      type="button"
                      class="mini-toggle-btn"
                      :class="{
                        'mini-toggle-btn--active': calendarMode === 'english',
                      }"
                      @click="
                        calendarMode = 'english';
                        calendarModeChanged();
                      "
                    >
                      English
                    </button>
                  </div>
                  <v-select
                    v-if="calendarMode === 'hebrew'"
                    :items="
                      calendarDays.filter((c) => c.HEBREW).map((c) => c.HEBREW)
                    "
                    label="Day"
                    v-model="dayInMonth"
                    @change="generateSchedules"
                    style="max-width: 80px;"
                    hide-details
                  ></v-select>
                  <v-select
                    v-if="calendarMode === 'english'"
                    :items="calendarDays.map((c) => c.value)"
                    label="Day"
                    v-model="dayInMonth"
                    @change="generateSchedules"
                    style="max-width: 80px;"
                    hide-details
                  ></v-select>
                </v-layout>

                <!-- Schedule error -->
                <div v-if="scheduleError" class="caption error--text mt-1 mb-1">
                  <v-icon small color="error">mdi-alert-circle-outline</v-icon>
                  {{ scheduleError }}
                </div>

                <!-- Generated schedule rows (scrollable) -->
                <div
                  v-if="schedules.length > 0"
                  class="schedule-rows-container mt-2"
                >
                  <div
                    v-for="(schedule, index) in schedules"
                    :key="index"
                    class="schedule-row-item"
                  >
                    <span class="schedule-row-num">#{{ index + 1 }}</span>
                    <v-text-field
                      style="width: 90px; flex-shrink: 0;"
                      v-model="schedule.amount"
                      label="Amount"
                      type="number"
                      prefix="$"
                      hide-details
                    ></v-text-field>
                    <v-menu
                      v-model="schedule.datePickerMenu"
                      :close-on-content-click="true"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          style="width: 120px; flex-shrink: 0;"
                          v-model="schedule.date"
                          label="Date"
                          prepend-icon="mdi-calendar"
                          readonly
                          hide-details
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="schedule.date"
                        :min="startDate || startDateMin"
                      ></v-date-picker>
                    </v-menu>
                    <span
                      v-if="schedule.hebrewDate"
                      class="schedule-row-hebrew"
                      >{{ schedule.hebrewDate }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </v-flex>

          <!-- SECTION 3: Payment Method -->
          <v-flex xs12 md4 class="section-col">
            <div class="cp-section">
              <div class="section-label mb-3">
                <v-icon small color="success" class="mr-1"
                  >mdi-credit-card</v-icon
                >
                Payment Method
              </div>

              <!-- Account loaded -->
              <div v-if="hasAccountLoaded">
                <v-select
                  box
                  :items="paymentMethods.map((p) => p.cardInfo)"
                  v-model="selectedCard"
                  label="Saved Card *"
                  ref="paymentMethod"
                  :rules="paymentMethodRules"
                  prepend-icon="mdi-credit-card"
                  :no-data-text="'No saved cards'"
                  class="mb-1"
                ></v-select>
                <v-btn
                  small
                  outline
                  color="success"
                  class="mt-0"
                  @click="newPaymentMethodDialog = true"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  New Payment Method
                </v-btn>
              </div>

              <!-- No account: inline card entry -->
              <div v-else>
                <v-select
                  box
                  :items="['Credit Card', 'Bank Account']"
                  v-model="cardType"
                  label="Payment Type"
                  hide-details
                  class="mb-3"
                ></v-select>

                <div v-if="cardType === 'Credit Card'">
                  <v-text-field
                    box
                    label="Card Number *"
                    ref="cardNumber"
                    :mask="creditCardMask"
                    v-model="cardNumber"
                    :rules="cardNumberRules"
                    append-icon="mdi-credit-card-outline"
                    class="mb-1"
                  ></v-text-field>
                  <v-layout row>
                    <v-flex xs6 class="pr-2">
                      <v-select
                        box
                        ref="expirationMonth"
                        :items="getMonthOptions()"
                        label="Exp. Month *"
                        v-model="expirationMonth"
                        :rules="expirationMonthRules"
                        class="mb-1"
                      ></v-select>
                    </v-flex>
                    <v-flex xs6 class="pl-2">
                      <v-select
                        box
                        :items="getYearOptions()"
                        ref="expirationYear"
                        v-model="expirationYear"
                        :rules="expirationYearRules"
                        label="Exp. Year *"
                        class="mb-1"
                      ></v-select>
                    </v-flex>
                  </v-layout>
                  <v-text-field
                    box
                    label="Security Code"
                    ref="securityCode"
                    v-model="securityCode"
                    :mask="securityCodeMask"
                    hide-details
                  ></v-text-field>
                </div>

                <div v-if="cardType === 'Bank Account'">
                  <v-text-field
                    box
                    label="Name on Account *"
                    ref="nameOnAccount"
                    v-model="nameOnAccount"
                    :rules="nameOnAccountRules"
                    class="mb-1"
                  ></v-text-field>
                  <v-text-field
                    box
                    label="Routing # *"
                    ref="routingNumber"
                    :mask="'#########'"
                    v-model="routingNumber"
                    :rules="routingNumberRules"
                    class="mb-1"
                  ></v-text-field>
                  <v-text-field
                    box
                    label="Account # *"
                    ref="accountNumber"
                    v-model="accountNumber"
                    :rules="accountNumberRules"
                    class="mb-1"
                  ></v-text-field>
                  <v-text-field
                    box
                    label="Confirm Account # *"
                    ref="confirmAccountNumber"
                    v-model="confirmAccountNumber"
                    :rules="confirmAccountNumberRules"
                  ></v-text-field>
                </div>
              </div>

              <v-checkbox
                v-if="paymentMode === 'schedule'"
                v-model="dontAuthorize"
                label="Don't Authorize Card"
                hide-details
                color="success"
                class="mt-2"
              ></v-checkbox>
            </div>
          </v-flex>
        </v-layout>

        <div class="sticky-actions">
          <span v-if="formError" class="caption error--text mr-3">
            <v-icon small color="error" class="mr-1"
              >mdi-alert-circle-outline</v-icon
            >{{ formError }}
          </span>
          <v-btn color="success" @click="confirmPayment()">Continue</v-btn>
        </div>
      </div>

      <!-- ── CONFIRM VIEW ────────────────────────────────── -->
      <div v-if="showConfirmView">
        <div v-if="!paymentProcessed && !scheduleProcessed">
          <div class="confirm-heading mb-4">
            <v-icon color="warning" class="mr-2"
              >mdi-clipboard-check-outline</v-icon
            >
            Please confirm the details below
          </div>

          <!-- Confirm sections row -->
          <v-layout row wrap align-start class="sections-row">
            <!-- Payer -->
            <v-flex xs12 md4 class="section-col">
              <div class="cp-section">
                <div class="section-label mb-3">
                  <v-icon small color="success" class="mr-1"
                    >mdi-account</v-icon
                  >
                  Payer
                </div>
                <div class="confirm-row">
                  <span class="confirm-key">Name</span
                  ><span class="confirm-val">{{ name }}</span>
                </div>
                <div class="confirm-row" v-if="phoneNumber">
                  <span class="confirm-key">Phone</span
                  ><span class="confirm-val">{{
                    formatPhoneNumber(phoneNumber)
                  }}</span>
                </div>
                <div class="confirm-row" v-if="emailAddress">
                  <span class="confirm-key">Email Receipt To</span
                  ><span class="confirm-val">{{ emailAddress }}</span>
                </div>
                <div class="confirm-row" v-if="address">
                  <span class="confirm-key">Address</span
                  ><span class="confirm-val">{{ address }}</span>
                </div>
              </div>
            </v-flex>

            <!-- Payment details -->
            <v-flex xs12 md4 class="section-col">
              <div class="cp-section">
                <div class="section-label mb-3">
                  <v-icon small color="success" class="mr-1"
                    >mdi-currency-usd</v-icon
                  >
                  Payment Details
                </div>
                <div class="confirm-row" v-if="getDepartmentName()">
                  <span class="confirm-key">Department</span
                  ><span class="confirm-val">{{ getDepartmentName() }}</span>
                </div>
                <div class="confirm-row">
                  <span class="confirm-key">Note</span
                  ><span class="confirm-val">{{ note }}</span>
                </div>

                <!-- One-time: just show amount -->
                <div class="confirm-row" v-if="paymentMode === 'single'">
                  <span class="confirm-key">Amount</span>
                  <span class="confirm-val confirm-val--amount">{{
                    formatAsCurrency(getCurrentAmount())
                  }}</span>
                </div>

                <!-- Schedule: show first-charge date + per-payment + schedule rows + total -->
                <div v-if="hasScheduledPayments()">
                  <!-- First charge row — only show when the first charge is today -->
                  <div
                    class="confirm-row"
                    v-if="getFirstChargeDetails() && getFirstChargeDetails().isToday"
                  >
                    <span class="confirm-key">First Charge</span>
                    <span
                      class="confirm-val"
                      style="display:flex;align-items:center;gap:6px;"
                    >
                      <span class="confirm-today-badge">Today</span>
                      {{
                        getFirstChargeDetails().date
                      }}
                      &mdash;
                      <span
                        class="confirm-val--amount"
                        style="font-size:14px;"
                        >{{
                          formatAsCurrency(getFirstChargeDetails().amount)
                        }}</span
                      >
                    </span>
                  </div>

                  <!-- Schedule rows header -->
                  <div
                    class="confirm-key mt-3 mb-1"
                    style="display:flex;align-items:center;gap:4px;"
                  >
                    <v-icon x-small color="success">mdi-calendar-clock</v-icon>
                    All {{ getScheduleConfirmationDetails().length }} payments
                  </div>
                  <div class="confirm-schedule-grid">
                    <div
                      v-for="(item, i) in getScheduleConfirmationDetails()"
                      :key="i"
                      class="confirm-schedule-item"
                      :class="{ 'confirm-schedule-item--today': item.isToday }"
                    >
                      <span class="schedule-row-num">#{{ i + 1 }}</span>
                      <span class="confirm-schedule-date">{{ item.date }}</span>
                      <span v-if="item.isToday" class="confirm-today-badge"
                        >Today</span
                      >
                      <span class="confirm-schedule-amount">{{
                        item.amount
                      }}</span>
                    </div>
                  </div>

                  <!-- Grand total (schedule) -->
                  <div class="confirm-grand-total mt-2">
                    <span>Total amount</span>
                    <span>{{ formatAsCurrency(getConfirmTotalAmount()) }}</span>
                  </div>
                </div>
              </div>
            </v-flex>

            <!-- Payment method -->
            <v-flex xs12 md4 class="section-col">
              <div class="cp-section">
                <div class="section-label mb-3">
                  <v-icon small color="success" class="mr-1"
                    >mdi-credit-card</v-icon
                  >
                  Payment Method
                </div>
                <div class="confirm-row">
                  <span class="confirm-key">Method</span>
                  <span class="confirm-val">{{
                    hasAccountLoaded ? selectedCard : cardType
                  }}</span>
                </div>
                <div v-if="!hasAccountLoaded && cardType === 'Credit Card'">
                  <div class="confirm-row">
                    <span class="confirm-key">Card</span
                    ><span class="confirm-val"
                      >**** **** ****
                      {{ cardNumber.replace(/\s/g, "").slice(-4) }}</span
                    >
                  </div>
                  <div class="confirm-row">
                    <span class="confirm-key">Expires</span
                    ><span class="confirm-val"
                      >{{ expirationMonth }}/{{ expirationYear }}</span
                    >
                  </div>
                </div>
                <div v-if="!hasAccountLoaded && cardType === 'Bank Account'">
                  <div class="confirm-row">
                    <span class="confirm-key">Name</span
                    ><span class="confirm-val">{{ nameOnAccount }}</span>
                  </div>
                  <div class="confirm-row">
                    <span class="confirm-key">Routing</span
                    ><span class="confirm-val">{{ routingNumber }}</span>
                  </div>
                  <div class="confirm-row">
                    <span class="confirm-key">Account</span
                    ><span class="confirm-val"
                      >****{{ accountNumber.slice(-4) }}</span
                    >
                  </div>
                </div>
              </div>
            </v-flex>
          </v-layout>

          <div class="sticky-actions">
            <span v-if="processingMessage" class="caption success--text mr-3">
              <v-icon small color="success" class="mr-1"
                >mdi-loading mdi-spin</v-icon
              >{{ processingMessage }}
            </span>
            <v-spacer></v-spacer>
            <v-btn outline @click="showConfirmView = false" class="mr-2"
              >Back</v-btn
            >
            <v-btn color="success" :loading="processing" @click="process()"
              >Confirm</v-btn
            >
          </div>
        </div>

        <!-- ── RESULT VIEW ─────────────────────────────── -->
        <div v-if="paymentProcessed || scheduleProcessed">
          <div v-if="+getCurrentAmount() > 0" class="px-3">
            <div v-if="paymentStatus === 'A'">
              <v-layout justify-center>
                <v-icon
                  style="height: 100px;"
                  size="100"
                  color="#A5DC86"
                  class="mt-2"
                  >mdi-check-circle-outline</v-icon
                >
              </v-layout>
              <v-layout justify-center>
                <v-card-title
                  style="color: #4caf50"
                  class="font-weight-medium title"
                  >Payment Approved</v-card-title
                >
              </v-layout>
            </div>
            <div v-if="paymentStatus === 'D'">
              <v-layout justify-center>
                <v-icon
                  style="height: 100px;"
                  size="100"
                  class="mt-2"
                  color="rgb(243, 81, 35)"
                  >mdi-alert</v-icon
                >
              </v-layout>
              <v-layout justify-center>
                <v-card-title class="font-weight-medium title text-danger mb-2"
                  >Payment Declined</v-card-title
                >
              </v-layout>
              <v-layout justify-center>
                <div class="text-danger mb-2">{{ paymentResult }}</div>
              </v-layout>
            </div>
            <div v-if="paymentStatus === 'E'">
              <v-layout justify-center>
                <v-icon
                  style="height: 100px;"
                  size="100"
                  class="mt-2"
                  color="rgb(243, 81, 35)"
                  >mdi-alert-circle</v-icon
                >
              </v-layout>
              <v-layout justify-center>
                <v-card-title class="font-weight-medium title"
                  >An Error Occurred</v-card-title
                >
              </v-layout>
              <v-layout justify-center>
                <div class="text-danger text-xs-center">
                  {{ paymentResult }}
                </div>
              </v-layout>
            </div>
          </div>

          <div v-if="hasScheduledPayments() && scheduleProcessed">
            <v-divider class="mb-3" v-if="+getCurrentAmount() > 0"></v-divider>
            <div v-if="!scheduleError">
              <v-layout justify-center>
                <v-icon
                  style="height: 100px;"
                  size="100"
                  color="#A5DC86"
                  class="mt-2"
                  >mdi-check-circle-outline</v-icon
                >
              </v-layout>
              <v-layout justify-center>
                <v-card-title
                  style="color: #4caf50"
                  class="font-weight-medium title"
                  >Payments Scheduled</v-card-title
                >
              </v-layout>
              <v-layout justify-center>
                <div style="width:100%;max-width:420px;" class="px-3">
                  <v-divider class="mb-3"></v-divider>
                  <v-layout row class="my-3">
                    <div class="font-weight-medium">DATE</div>
                    <v-spacer></v-spacer>
                    <div class="font-weight-medium">AMOUNT</div>
                  </v-layout>
                  <v-divider class="mb-3"></v-divider>
                  <div
                    v-for="(schedule, index) in getSchedulePayments()"
                    :key="index"
                  >
                    <v-layout row class="my-3">
                      <div>{{ schedule.date | date }}</div>
                      <v-spacer></v-spacer>
                      <div>{{ schedule.amount | currency }}</div>
                    </v-layout>
                    <v-divider
                      v-if="index + 1 < getSchedulePayments().length"
                    ></v-divider>
                  </div>
                </div>
              </v-layout>
            </div>
            <div v-if="scheduleError">
              <v-layout justify-center>
                <v-icon
                  style="height: 100px;"
                  size="100"
                  class="mt-2"
                  color="rgb(243, 81, 35)"
                  >mdi-alert-circle</v-icon
                >
              </v-layout>
              <v-layout justify-center>
                <v-card-title class="font-weight-medium title"
                  >{{
                    isScheduleAuthError
                      ? "Card authorization failed"
                      : "An error occurred setting up the schedule"
                  }}</v-card-title
                >
              </v-layout>
              <div
                class="text-danger text-xs-center px-4"
                style="max-width: 560px; margin: 0 auto;"
              >
                {{ scheduleError }}
              </div>
              <div
                v-if="isScheduleAuthError"
                class="caption text-xs-center grey--text mt-2 px-4"
                style="max-width: 560px; margin: 0 auto;"
              >
                Go back and check Don't Authorize Card to save the schedule
                without a $1 verification.
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-card-actions
        class="mr-0 pb-3 result-actions"
        v-if="paymentProcessed || scheduleProcessed"
      >
        <v-btn color="success" @click="reset()">
          <v-icon left small>mdi-plus</v-icon>New Payment
        </v-btn>
        <v-btn
          v-if="hasAnyProcessingError()"
          color="default"
          dark
          @click="
            processingMessage = '';
            paymentProcessed = false;
            scheduleProcessed = false;
            scheduleError = '';
            showConfirmView = false;
          "
          >Try Again</v-btn
        >
        <v-btn
          v-if="!allowAdminFeatures"
          outline
          color="success"
          @click="$router.push({ name: 'UtaDashboard' })"
          >Go to Dashboard</v-btn
        >
        <v-btn
          v-else
          outline
          color="success"
          @click="
            $router.push({
              name: isScheduleOnlyResult() ? 'Schedules' : 'Payments',
            })
          "
          >{{
            isScheduleOnlyResult() ? "Go to Schedules" : "Go to Payments"
          }}</v-btn
        >
      </v-card-actions>
    </v-card>

    <!-- ── History Section ──────────────────────────────────── -->
    <v-card class="ma-3 mt-2 pb-3">
      <div class="history-tabs-bar">
        <v-tabs
          v-model="historyTab"
          color="white"
          slider-color="white"
          class="history-tabs ml-3"
        >
          <v-tab class="history-tab-item">
            <v-icon small class="mr-1">mdi-credit-card</v-icon>
            Payments
            <span class="history-count-badge ml-2">{{
              filteredHistoryPayments.length
            }}</span>
          </v-tab>
          <v-tab class="history-tab-item">
            <v-icon small class="mr-1">mdi-calendar-clock</v-icon>
            Schedules
            <span class="history-count-badge ml-2">{{
              filteredHistorySchedules.length
            }}</span>
          </v-tab>
        </v-tabs>
        <v-btn
          flat
          icon
          small
          :loading="historyLoading"
          @click="loadHistory()"
          title="Refresh"
          class="history-refresh-btn"
        >
          <v-icon small color="white">mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Payments tab -->
      <v-tabs-items v-model="historyTab" class="px-3">
        <v-tab-item>
          <!-- Filters -->
          <v-layout row wrap align-center class="pt-3 pb-1">
            <v-flex xs12 sm4 class="pr-2">
              <v-text-field
                v-model="historyPaymentSearch"
                placeholder="Search name..."
                prepend-icon="mdi-magnify"
                clearable
                hide-details
                dense
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3 class="pr-2">
              <v-select
                v-model="historyPaymentStatus"
                :items="['All', 'PROCESSED', 'DECLINED', 'ERROR']"
                label="Status"
                hide-details
                dense
                clearable
              ></v-select>
            </v-flex>
            <v-flex xs12 sm3>
              <v-select
                v-model="historyPaymentDays"
                :items="[
                  { text: 'Last 7 days', value: 7 },
                  { text: 'Last 30 days', value: 30 },
                  { text: 'Last 90 days', value: 90 },
                  { text: 'All time', value: 0 },
                ]"
                label="Period"
                hide-details
                dense
              ></v-select>
            </v-flex>
          </v-layout>

          <div v-if="historyLoading" class="text-xs-center py-4">
            <v-progress-circular
              indeterminate
              color="success"
              size="32"
            ></v-progress-circular>
          </div>
          <div
            v-else-if="filteredHistoryPayments.length === 0"
            class="history-empty"
          >
            <v-icon color="grey lighten-1" size="36">mdi-cash-remove</v-icon>
            <div class="grey--text mt-1">No payments found</div>
          </div>
          <div v-else class="history-table-wrap">
            <table class="history-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Card</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in filteredHistoryPayments" :key="p.paymentID">
                  <td>
                    <v-icon
                      small
                      :color="historyPaymentStatusColor(p.statusValue)"
                    >
                      {{ historyPaymentStatusIcon(p.statusValue, p.isVoided) }}
                    </v-icon>
                  </td>
                  <td class="history-date">{{ p.date | date }}</td>
                  <td>{{ p.name }}</td>
                  <td class="history-amount">{{ p.amount | currency }}</td>
                  <td>{{ p.paymentType }}</td>
                  <td class="history-card">
                    <span v-if="p.cardType">{{ p.cardType }}</span>
                    <span v-if="p.cardNumLast5" class="grey--text ml-1"
                      >···{{ p.cardNumLast5 }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="history-status-badge"
                      :class="
                        'history-status--' +
                          (p.statusValue || 'unknown').toLowerCase()
                      "
                    >
                      {{ p.isVoided ? "VOIDED" : p.statusValue }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-tab-item>

        <!-- Schedules tab -->
        <v-tab-item>
          <!-- Filters -->
          <v-layout row wrap align-center class="pt-3 pb-1">
            <v-flex xs12 sm4 class="pr-2">
              <v-text-field
                v-model="historyScheduleSearch"
                placeholder="Search name..."
                prepend-icon="mdi-magnify"
                clearable
                hide-details
                dense
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3>
              <v-select
                v-model="historyScheduleStatus"
                :items="['All', 'Scheduled', 'Completed']"
                label="Status"
                hide-details
                dense
                clearable
              ></v-select>
            </v-flex>
          </v-layout>

          <div v-if="historyLoading" class="text-xs-center py-4">
            <v-progress-circular
              indeterminate
              color="success"
              size="32"
            ></v-progress-circular>
          </div>
          <div
            v-else-if="filteredHistorySchedules.length === 0"
            class="history-empty"
          >
            <v-icon color="grey lighten-1" size="36"
              >mdi-calendar-remove</v-icon
            >
            <div class="grey--text mt-1">No schedules found</div>
          </div>
          <div v-else class="history-table-wrap">
            <table class="history-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Name</th>
                  <th>Total</th>
                  <th>Type</th>
                  <th>Card</th>
                  <th>Payments</th>
                  <th>Next Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in filteredHistorySchedules" :key="s.scheduleID">
                  <td class="history-date">{{ s.dateCreated | date }}</td>
                  <td>{{ s.name }}</td>
                  <td class="history-amount">{{ s.totalAmount | currency }}</td>
                  <td>{{ s.paymentType }}</td>
                  <td class="history-card">
                    <span v-if="s.cardType">{{ s.cardType }}</span>
                    <span v-if="s.cardNumLast5" class="grey--text ml-1"
                      >···{{ s.cardNumLast5 }}</span
                    >
                  </td>
                  <td>
                    <span class="history-sched-pills">
                      <span v-if="s.processed" class="sched-pill sched-pill--ok"
                        >✓ {{ s.processed }}</span
                      >
                      <span
                        v-if="s.scheduled"
                        class="sched-pill sched-pill--pending"
                        >⏳ {{ s.scheduled }}</span
                      >
                      <span v-if="s.declined" class="sched-pill sched-pill--bad"
                        >✗ {{ s.declined }}</span
                      >
                    </span>
                  </td>
                  <td class="history-date">{{ s.nextScheduleDate | date }}</td>
                  <td>
                    <span
                      class="history-status-badge"
                      :class="
                        'history-status--' +
                          (s.status || 'unknown').toLowerCase()
                      "
                    >
                      {{ s.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- ── Account Lookup Dialog ──────────────────────────── -->
    <v-dialog v-model="accountLookupDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="success white--text py-2">
          <v-icon color="white" class="mr-2">mdi-account-search</v-icon>
          <span class="title pt-2 pb-0 mb-0">Account Lookup</span>
          <v-spacer></v-spacer>
          <v-btn class="my-0 white--text" icon @click="closeAccountLookup()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4 pb-2">
          <v-layout row wrap>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="lookupPhone"
                label="Phone Number"
                outlined
                dense
                prepend-icon="mdi-phone"
                clearable
                autocomplete="new-password"
                placeholder="Enter phone number"
                @input="debouncedLookupSearch()"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-alert :value="lookupLoading" type="info" outline class="mt-0 mb-2"
            >Searching...</v-alert
          >
          <v-alert
            :value="showNoLookupResults && !lookupLoading"
            type="info"
            outline
            class="mt-0"
            >No results found.</v-alert
          >
        </v-card-text>

        <div
          v-if="lookupResults.length > 0 && !lookupLoading"
          class="person-list-container"
        >
          <v-list two-line class="person-list">
            <v-list-tile
              v-for="(person, index) in lookupResults"
              :key="'p-' + index"
              @click="selectPerson(person)"
              class="person-item"
            >
              <v-list-tile-content>
                <v-list-tile-title class="person-name mb-1 pb-2">
                  <span class="font-weight-medium">
                    {{ person.lastEnglish || person.lastName }},
                    {{ person.firstEnglish || person.firstName }}
                  </span>
                  <span
                    v-if="person.firstHebrew || person.lastHebrew"
                    class="grey--text ml-2"
                  >
                    / {{ person.lastHebrew }}, {{ person.firstHebrew }}
                  </span>
                </v-list-tile-title>
                <v-list-tile-sub-title class="person-details mt-1">
                  <div class="detail-line" v-if="person.parentId">
                    <v-icon small class="detail-icon">mdi-account</v-icon>
                    <span>{{ person.parentId }}</span>
                  </div>
                  <div v-if="person.address1" class="detail-line">
                    <v-icon small class="detail-icon">mdi-home</v-icon>
                    <span
                      >{{ person.address1
                      }}<span v-if="person.city">, {{ person.city }}</span
                      ><span v-if="person.zip"> {{ person.zip }}</span></span
                    >
                  </div>
                  <div
                    v-if="person.father || person.fatherInLaw"
                    class="detail-line"
                  >
                    <span v-if="person.father" class="mr-3">
                      <v-icon small class="detail-icon">mdi-account-tie</v-icon
                      >Father: {{ person.father }}
                    </span>
                    <span v-if="person.fatherInLaw">
                      <v-icon small class="detail-icon"
                        >mdi-account-tie-outline</v-icon
                      >Shver: {{ person.fatherInLaw }}
                    </span>
                  </div>
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-icon color="success">mdi-chevron-right</v-icon>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </div>
      </v-card>
    </v-dialog>

    <!-- New Payment Method Dialog -->
    <v-dialog v-model="newPaymentMethodDialog" max-width="400">
      <new-payment-method
        :accountID="selectedAccountId"
        :closeDialog="closeNewPaymentMethodDialog"
        :onSave="updateSelectedPaymentMethod"
      ></new-payment-method>
    </v-dialog>

    <!-- Card Expiry Warning Dialog -->
    <v-dialog v-model="cardExpiryWarningDialog" max-width="420" persistent>
      <v-card class="card-expiry-warn-card">
        <div class="card-expiry-warn-header">
          <v-icon color="warning" size="28" class="mr-2"
            >mdi-credit-card-clock-outline</v-icon
          >
          <span class="card-expiry-warn-title">Card Expiring Soon</span>
        </div>
        <v-card-text class="card-expiry-warn-body">
          <div class="card-expiry-warn-message">
            {{ cardExpiryWarningMessage }}
          </div>
        </v-card-text>
        <v-card-actions class="mb-2">
          <v-spacer></v-spacer>
          <v-btn
            outline
            color="grey darken-1"
            @click="resolveCardExpiryWarning(false)"
          >
            Cancel
          </v-btn>
          <v-btn
            color="warning"
            dark
            depressed
            @click="resolveCardExpiryWarning(true)"
          >
            <v-icon left small>mdi-check</v-icon>
            Continue Anyway
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import cardValidator from "card-validator";
import {
  formatCurrency,
  isCharityCard,
  sum,
  toISOLocal,
  onlyUnique,
} from "../../utils/helpers.js";
import newPaymentMethod from "./NewPaymentMethod.vue";
import * as Hebcal from "hebcal";

export default {
  components: { newPaymentMethod },

  data() {
    return {
      paymentMode: "single",
      scheduleMode: "month",
      showConfirmView: false,
      processing: false,
      processingMessage: "",

      name: "",
      phoneNumber: "",
      emailAddress: "",
      address: "",
      note: "",

      amount: 0,
      selectedDepartmentId: null,
      departments: [],
      loadingDepartments: false,

      selectedCard: "",
      paymentMethods: [],
      selectedAccountId: null,
      selectedPersonId: null,
      newPaymentMethodDialog: false,

      // Inline card entry
      cardType: "Credit Card",
      cardNumber: "",
      expirationMonth: "",
      expirationYear: "",
      securityCode: "",
      nameOnAccount: "",
      routingNumber: "",
      accountNumber: "",
      confirmAccountNumber: "",

      numberOfPayments: "2",
      scheduleEvery: "1",
      schedules: [],
      startDate: toISOLocal(new Date()).substr(0, 10),
      today: toISOLocal(new Date()).substr(0, 10),
      startDateMin: toISOLocal(new Date()).substr(0, 10),
      datePickerMenu: false,
      showCalendarDays: false,
      calendarMode: "hebrew",
      dayInMonth: "",
      dontAuthorize: false,

      paymentProcessed: false,
      scheduleProcessed: false,
      paymentStatus: "",
      paymentResult: "",
      scheduleError: "",
      formError: "",

      selectedPersonLoaded: false,
      loadingAccountDetails: false,

      historyTab: 0,
      historyLoading: false,
      historyPayments: [],
      historySchedules: [],
      historyPaymentSearch: "",
      historyPaymentStatus: "All",
      historyPaymentDays: 30,
      historyScheduleSearch: "",
      historyScheduleStatus: "All",
      forceBasicMode: false,

      cardExpiryWarningDialog: false,
      cardExpiryWarningMessage: "",
      _cardExpiryResolve: null,

      accountLookupDialog: false,
      lookupPhone: "",
      lookupResults: [],
      lookupLoading: false,
      showNoLookupResults: false,
      lookupDebounceTimer: null,

      calendarDays: [
        { value: 1, HEBREW: "א" },
        { value: 2, HEBREW: "ב" },
        { value: 3, HEBREW: "ג" },
        { value: 4, HEBREW: "ד" },
        { value: 5, HEBREW: "ה" },
        { value: 6, HEBREW: "ו" },
        { value: 7, HEBREW: "ז" },
        { value: 8, HEBREW: "ח" },
        { value: 9, HEBREW: "ט" },
        { value: 10, HEBREW: "י" },
        { value: 11, HEBREW: "יא" },
        { value: 12, HEBREW: "יב" },
        { value: 13, HEBREW: "יג" },
        { value: 14, HEBREW: "יד" },
        { value: 15, HEBREW: "טו" },
        { value: 16, HEBREW: "טז" },
        { value: 17, HEBREW: "יז" },
        { value: 18, HEBREW: "יח" },
        { value: 19, HEBREW: "יט" },
        { value: 20, HEBREW: "כ" },
        { value: 21, HEBREW: "כא" },
        { value: 22, HEBREW: "כב" },
        { value: 23, HEBREW: "כג" },
        { value: 24, HEBREW: "כד" },
        { value: 25, HEBREW: "כה" },
        { value: 26, HEBREW: "כו" },
        { value: 27, HEBREW: "כז" },
        { value: 28, HEBREW: "כח" },
        { value: 29, HEBREW: "כט" },
        { value: 30, HEBREW: "ל" },
        { value: 31 },
      ],

      nameRules: [(v) => !!v || "Name is required"],
      phoneRules: [
        (v) => !v || /^[\d\s\-()+.]{7,20}$/.test(v) || "Invalid phone number",
      ],
      noteRules: [(v) => !!v || "Note is required"],
      departmentRules: [(v) => !!v || "Department is required"],
      paymentMethodRules: [(v) => !!v || "Payment method is required"],
      numberOfPaymentsRules: [
        (v) => !!v || this.paymentMode === "single" || "Required",
        (v) => !v || v > 0 || "Minimum 1",
      ],
      scheduleEveryRules: [
        (v) => !v || +v > 0 || "Minimum 1",
      ],
      startDateRules: [
        (v) => !!v || "Start date is required",
        (v) =>
          !v || moment(v).isSameOrAfter(moment(this.today), "day")
            ? true
            : "Start date cannot be before today",
      ],
      amountRules: [
        (v) => +v > 0 || "Amount must be greater than 0",
        (v) => !v || +v >= 0 || "Amount can't be negative",
      ],
      emailRules: [
        (v) => {
          if (!v) return true;
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(v) || "Invalid e-mail.";
        },
      ],
      cardNumberRules: [
        (v) => !!v || "Card Number is required",
        (v) => (v && this.validateCard()) || "Invalid card number",
      ],
      expirationMonthRules: [
        (v) => !!v || "Required",
        (v) =>
          (v && cardValidator.expirationMonth(v).isValid) || "Invalid month",
      ],
      expirationYearRules: [
        (v) => !!v || "Required",
        (v) => (v && cardValidator.expirationYear(v).isValid) || "Invalid year",
      ],
      nameOnAccountRules: [(v) => !!v || "Account name is required"],
      routingNumberRules: [
        (v) => !!v || "Routing number is required",
        (v) => (v && v.length === 9) || "Must be 9 digits",
      ],
      accountNumberRules: [(v) => !!v || "Account number is required"],
      confirmAccountNumberRules: [
        (v) => !!v || "Required",
        (v) => (v && this.accountNumber === v) || "Account numbers don't match",
      ],
    };
  },

  async created() {
    this.forceBasicMode =
      this.$route &&
      this.$route.query &&
      (this.$route.query.mode || "").toString().toLowerCase() === "basic";
    this.scheduleMode = "month";
    await this.loadDepartments();
    this.loadHistory();
  },

  computed: {
    isAdmin() {
      const auth = this.$store.state.authentication || {};
      const isSystemAdmin = !!auth.isAdmin && auth.isAdmin !== "false";
      if (isSystemAdmin) return true;

      const rawClaims = auth.claims || "";
      const claims = Array.isArray(rawClaims)
        ? rawClaims
        : rawClaims.split(",");

      return claims.some((c) => (c || "").trim() === "CustomPayment");
    },
    allowAdminFeatures() {
      return this.isAdmin && !this.forceBasicMode;
    },
    hasAccountLoaded() {
      return !!this.selectedAccountId;
    },
    scheduleIntervalSuffix() {
      const unit = this.scheduleMode === "month" ? "month" : "week";
      return +this.scheduleEvery === 1 ? unit : unit + "s";
    },
    creditCardMask() {
      const nv = cardValidator.number(this.cardNumber);
      return nv.card && nv.card.type === "american-express"
        ? "#### ###### #####"
        : "#### #### #### ####";
    },
    securityCodeMask() {
      const nv = cardValidator.number(this.cardNumber);
      return nv.card && nv.card.code && nv.card.code.size === 4
        ? "####"
        : "###";
    },
    isScheduleAuthError() {
      const msg = (this.scheduleError || "").toLowerCase();
      return (
        msg.indexOf("authoriz") !== -1 ||
        msg.indexOf("don't authorize") !== -1
      );
    },

    filteredHistoryPayments() {
      const cutoff = this.historyPaymentDays
        ? moment().subtract(this.historyPaymentDays, "days")
        : null;
      const search = (this.historyPaymentSearch || "").toLowerCase();
      const status = this.historyPaymentStatus;
      return this.historyPayments.filter((p) => {
        if (cutoff && moment(p.date).isBefore(cutoff)) return false;
        if (status && status !== "All" && p.statusValue !== status)
          return false;
        if (search && !(p.name || "").toLowerCase().includes(search))
          return false;
        return true;
      });
    },

    filteredHistorySchedules() {
      const search = (this.historyScheduleSearch || "").toLowerCase();
      const status = this.historyScheduleStatus;
      return this.historySchedules.filter((s) => {
        if (
          status &&
          status !== "All" &&
          (s.status || "").toLowerCase() !== status.toLowerCase()
        )
          return false;
        if (search && !(s.name || "").toLowerCase().includes(search))
          return false;
        return true;
      });
    },
  },

  filters: {
    date(val) {
      if (!val) return "—";
      return moment(val).format("M/D/YY");
    },
    currency(val) {
      if (val === null || val === undefined || val === "") return "—";
      return (
        "$" +
        (+val).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
  },

  methods: {
    async loadDepartments() {
      this.loadingDepartments = true;
      try {
        const result = await this.$http.get("api/payments/GetDepartments");
        this.departments = (result || []).filter(
          (d) => !d.isHiddenInNewPayment
        );
        if (!this.allowAdminFeatures) {
          const donation = this.departments.find(
            (d) => d.name && d.name.toLowerCase().includes("donation")
          );
          if (donation) this.selectedDepartmentId = donation.departmentId;
        }
      } catch (e) {
        console.error("Failed to load departments", e);
      } finally {
        this.loadingDepartments = false;
      }
    },

    async loadHistory() {
      this.historyLoading = true;
      try {
        const user = this.$store.state.authentication.user;
        const result = await this.$http.get(
          "api/payments/GetCustomPaymentsAndSchedules?user=" +
            encodeURIComponent(user)
        );
        this.historyPayments =
          (result && (result.payments || result.Payments)) || [];
        this.historySchedules =
          (result && (result.schedules || result.Schedules)) || [];
      } catch (e) {
        console.error("Failed to load history", e);
      } finally {
        this.historyLoading = false;
      }
    },

    historyPaymentStatusIcon(statusValue, isVoided) {
      if (isVoided) return "mdi-cancel";
      if (statusValue === "PROCESSED") return "mdi-check-circle";
      if (statusValue === "DECLINED") return "mdi-alert";
      if (statusValue === "ERROR") return "mdi-alert-circle";
      return "mdi-circle-outline";
    },

    historyPaymentStatusColor(statusValue) {
      if (statusValue === "PROCESSED") return "success";
      if (statusValue === "DECLINED" || statusValue === "ERROR")
        return "#f35123";
      return "#aeb8bf";
    },

    getDepartmentName() {
      if (!this.selectedDepartmentId) return "";
      const dept = this.departments.find(
        (d) => d.departmentId === this.selectedDepartmentId
      );
      return dept ? dept.name : "";
    },

    formatAsCurrency(value) {
      return formatCurrency(value);
    },

    formatPhoneNumber(phone) {
      if (!phone) return "";
      const digits = phone.replace(/\D/g, "");
      if (digits.length === 10) {
        return (
          "(" +
          digits.slice(0, 3) +
          ") " +
          digits.slice(3, 6) +
          "-" +
          digits.slice(6)
        );
      }
      if (digits.length === 11 && digits[0] === "1") {
        return (
          "+1 (" +
          digits.slice(1, 4) +
          ") " +
          digits.slice(4, 7) +
          "-" +
          digits.slice(7)
        );
      }
      return phone;
    },

    // ── Card helpers ───────────────────────────────────────

    validateCard() {
      return (
        cardValidator.number(this.cardNumber).isValid ||
        isCharityCard(this.cardNumber)
      );
    },

    getMonthOptions() {
      const months = [
        { value: "01", text: "1 January" },
        { value: "02", text: "2 February" },
        { value: "03", text: "3 March" },
        { value: "04", text: "4 April" },
        { value: "05", text: "5 May" },
        { value: "06", text: "6 June" },
        { value: "07", text: "7 July" },
        { value: "08", text: "8 August" },
        { value: "09", text: "9 September" },
        { value: "10", text: "10 October" },
        { value: "11", text: "11 November" },
        { value: "12", text: "12 December" },
      ];
      if (this.expirationYear !== moment().format("YY")) return months;
      const currentMonth = new Date().getMonth();
      return months.filter((m) => +m.value >= currentMonth + 1);
    },

    getYearOptions() {
      const yearOptions = [];
      for (let i = 0; i < 10; i++) {
        const year = moment()
          .add(i, "years")
          .year();
        if (this.expirationMonth <= moment().month() + 1 && i === 0) continue;
        yearOptions.push({ text: year, value: year.toString().slice(-2) });
      }
      return yearOptions;
    },

    getInlineCardNumber() {
      if (this.cardType === "Credit Card")
        return (this.cardNumber || "").replace(/\s/g, "");
      return this.routingNumber + "|" + this.accountNumber;
    },

    // ── Validation ─────────────────────────────────────────

    async confirmPayment() {
      if (!this.validate()) {
        this.formError = "Please fill in all required fields.";
        return;
      }
      const canProceed = await this.checkCardExpirationWarning();
      if (!canProceed) return;
      this.formError = "";
      this.showConfirmView = true;
    },

    validate() {
      const validations = [
        this.$refs.name.validate(true),
        this.$refs.note.validate(true),
        this.$refs.amount.validate(true),
      ];
      if (this.allowAdminFeatures && this.$refs.department) {
        validations.push(this.$refs.department.validate(true));
      }

      if (this.$refs.phoneNumber) {
        validations.push(this.$refs.phoneNumber.validate(true));
      }
      if (this.$refs.emailAddress) {
        validations.push(this.$refs.emailAddress.validate(true));
      }

      if (this.hasAccountLoaded) {
        validations.push(this.$refs.paymentMethod.validate(true));
      } else {
        if (this.cardType === "Credit Card") {
          validations.push(
            this.$refs.cardNumber.validate(true),
            this.$refs.expirationMonth.validate(true),
            this.$refs.expirationYear.validate(true)
          );
        } else {
          validations.push(
            this.$refs.nameOnAccount.validate(true),
            this.$refs.routingNumber.validate(true),
            this.$refs.accountNumber.validate(true),
            this.$refs.confirmAccountNumber.validate(true)
          );
        }
      }

      if (this.paymentMode === "schedule") {
        validations.push(this.$refs.numberOfPayments.validate(true));
        if (this.$refs.startDate) {
          validations.push(this.$refs.startDate.validate(true));
        }
        if (this.$refs.scheduleEvery) {
          validations.push(this.$refs.scheduleEvery.validate(true));
        }
      }

      return validations.every(Boolean);
    },

    // ── Schedule ───────────────────────────────────────────

    generateSchedules() {
      if (this.showCalendarDays) return this.dayInMonthChanged();
      this.schedules = [];
      if (this.paymentMode !== "schedule") return;
      const perPayment = this.amount / this.numberOfPayments;
      const every = Math.max(1, +this.scheduleEvery || 1);
      for (var i = 0; i < this.numberOfPayments; i++) {
        const s = {
          date: new Date().toISOString().substr(0, 10),
          amount: 0,
          datePickerMenu: false,
        };
        s.amount = +perPayment.toFixed(2);
        s.date = toISOLocal(
          this.scheduleMode === "month"
            ? new Date(moment(this.startDate).add(i * every, "months"))
            : new Date(moment(this.startDate).add(i * every, "weeks"))
        ).substr(0, 10);
        this.schedules.push(s);
      }
    },

    calendarModeChanged() {
      this.dayInMonth = this.calendarMode === "hebrew" ? "א" : 1;
      this.generateSchedules();
    },

    startDateChanged() {
      if (
        !this.startDate ||
        moment(this.startDate).isBefore(moment(this.today), "day")
      ) {
        this.startDate = this.startDateMin;
      }
      this.generateSchedules();
    },

    dayInMonthChanged() {
      if (
        this.paymentMode !== "schedule" ||
        !this.dayInMonth ||
        !+this.numberOfPayments
      )
        return;
      return this.calendarMode === "hebrew"
        ? this.createSchedulesBasedOnHebDate()
        : this.createSchedulesBasedOnEngDate();
    },

    async createSchedulesBasedOnHebDate() {
      this.scheduleError = "";
      try {
        let hDates = await this.$http.get(
          "api/payments/GetHDates?from=" +
            this.startDate +
            "&months=" +
            this.numberOfPayments
        );
        this.schedules = [];
        const perPayment = this.amount / this.numberOfPayments;
        const dayNumber = +this.calendarDays.find(
          (c) => c.HEBREW == this.dayInMonth
        ).value;
        const hebDate = new Hebcal.HDate(new Date());
        if (dayNumber < hebDate.day) {
          hDates = hDates.filter(
            // eslint-disable-next-line eqeqeq
            (h) => !(h.mmonth == hebDate.month && h.yyear == hebDate.year)
          );
        }
        const scheduleHDates = hDates
          .map((h) => h.mmonth)
          .filter(onlyUnique)
          .map((month) => {
            // eslint-disable-next-line eqeqeq
            let hDate = hDates.find(
              (h) => h.mmonth == month && h.mday == dayNumber
            );
            if (!hDate) {
              // eslint-disable-next-line eqeqeq
              return hDates.find((h) => h.mmonth == +month + 1 && h.mday == 1);
            }
            return hDate;
          })
          .filter(Boolean);
        scheduleHDates.forEach((scheduleHDate, index) => {
          if (index + 1 > this.numberOfPayments) return;
          this.createSchedule(
            scheduleHDate.pardate,
            scheduleHDate.hdesc,
            +perPayment.toFixed(2)
          );
        });
        if (this.schedules.length === 0 && +this.numberOfPayments > 0) {
          this.scheduleError =
            "No Hebrew dates found. Try a different day or start date.";
        }
      } catch (e) {
        this.scheduleError =
          "Could not load Hebrew schedule. Please try again.";
        console.error(e);
      }
    },

    createSchedulesBasedOnEngDate() {
      this.schedules = [];
      const perPayment = this.amount / this.numberOfPayments;
      let startDate = new Date(this.startDate + "T00:00:00");
      if (startDate.getDate() > this.dayInMonth) {
        startDate = new Date(moment(startDate).add(1, "months"));
      }
      for (var i = 0; i < this.numberOfPayments; i++) {
        const s = {
          date: new Date().toISOString().substr(0, 10),
          amount: 0,
          datePickerMenu: false,
        };
        s.amount = +perPayment.toFixed(2);
        s.date = toISOLocal(
          new Date(
            startDate.getFullYear(),
            startDate.getUTCMonth() + i,
            this.dayInMonth
          )
        ).substr(0, 10);
        this.schedules.push(s);
      }
    },

    createSchedule(date, hebrewDate, scheduleAmount) {
      this.schedules.push({
        date: toISOLocal(new Date(date)).substr(0, 10),
        amount: scheduleAmount,
        datePickerMenu: false,
        hebrewDate,
      });
    },

    isScheduledPayment(date) {
      return !this.isToday(date);
    },
    isToday(date) {
      return moment(date).isSame(new Date(), "day");
    },

    getCurrentAmount() {
      if (this.paymentMode === "single") return +this.amount;
      return sum(
        this.schedules.filter((s) => this.isToday(s.date)).map((s) => +s.amount)
      );
    },

    getFirstChargeDetails() {
      if (this.paymentMode !== "schedule") return null;
      const payableSchedules = this.schedules.filter(
        (s) => s.date && +s.amount > 0
      );
      if (!payableSchedules.length) return null;

      const todaySchedules = payableSchedules.filter((s) =>
        this.isToday(s.date)
      );
      if (todaySchedules.length) {
        return {
          date: moment().format("L"),
          amount: sum(todaySchedules.map((s) => +s.amount)),
          isToday: true,
        };
      }

      const sortedSchedules = payableSchedules
        .slice()
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
      const firstSchedule = sortedSchedules[0];
      return {
        date: moment(firstSchedule.date).format("L"),
        amount: +firstSchedule.amount,
        isToday: this.isToday(firstSchedule.date),
      };
    },

    getScheduleDateRange() {
      const payableSchedules = this.schedules
        .filter((s) => s.date && +s.amount > 0)
        .map((s) => moment(s.date).startOf("day"))
        .filter((d) => d.isValid());
      if (!payableSchedules.length) return null;

      let start = payableSchedules[0].clone();
      let end = payableSchedules[0].clone();
      payableSchedules.forEach((d) => {
        if (d.isBefore(start)) start = d.clone();
        if (d.isAfter(end)) end = d.clone();
      });
      return { start, end };
    },

    getSelectedCardExpirationParts() {
      if (!this.hasAccountLoaded) {
        if (this.cardType !== "Credit Card") return null;
        if (!this.expirationMonth || !this.expirationYear) return null;
        return {
          month: this.expirationMonth.toString(),
          year: this.expirationYear.toString(),
        };
      }

      const selected = this.paymentMethods.find(
        (p) => p.cardInfo === this.selectedCard
      );
      if (!selected) return null;
      const typeText = (selected.cardType || "").toString().toLowerCase();
      const bankTypeHints = ["bank", "ach", "checking", "savings", "echeck", "ck"];
      const isLikelyBankType = bankTypeHints.some(
        (hint) => typeText.indexOf(hint) !== -1
      );
      if (isLikelyBankType) return null;

      let month =
        selected.expirationMonth ||
        selected.expMonth ||
        selected.expirationmonth ||
        selected.expmonth ||
        selected.cardExpirationMonth ||
        selected.month ||
        "";
      let year =
        selected.expirationYear ||
        selected.expYear ||
        selected.expirationyear ||
        selected.expyear ||
        selected.cardExpirationYear ||
        selected.year ||
        "";

      const expirationValue =
        selected.expiration ||
        selected.expDate ||
        selected.expirationDate ||
        selected.cardExpiration ||
        "";

      if ((!month || !year) && expirationValue) {
        const expDigits = expirationValue.toString().replace(/\D/g, "");
        if (expDigits.length >= 4) {
          month = expDigits.slice(0, 2);
          year = expDigits.slice(-2);
        }
      }

      if (!month || !year) return null;
      return { month: month.toString(), year: year.toString() };
    },

    getCardExpirationMoment(parts) {
      if (!parts || !parts.month || !parts.year) return null;
      const month = parseInt(parts.month, 10);
      let year = parseInt(parts.year, 10);
      if (!month || month < 1 || month > 12 || Number.isNaN(year)) return null;
      if (year < 100) year += 2000;
      return moment({ year, month: month - 1, day: 1 }).endOf("month");
    },

    checkCardExpirationWarning() {
      if (this.paymentMode !== "schedule") return Promise.resolve(true);
      const range = this.getScheduleDateRange();
      if (!range) return Promise.resolve(true);

      const expirationParts = this.getSelectedCardExpirationParts();
      const expirationMoment = this.getCardExpirationMoment(expirationParts);
      if (!expirationMoment) return Promise.resolve(true);

      const expiresWithinRange =
        expirationMoment.isSameOrAfter(range.start, "day") &&
        expirationMoment.isSameOrBefore(range.end, "day");
      if (!expiresWithinRange) return Promise.resolve(true);

      this.cardExpiryWarningMessage =
        "This credit card expires " +
        expirationMoment.format("MM/YYYY") +
        ", which falls within this schedule (" +
        range.start.format("M/D/YYYY") +
        " \u2013 " +
        range.end.format("M/D/YYYY") +
        ").";

      this.cardExpiryWarningDialog = true;

      return new Promise((resolve) => {
        this._cardExpiryResolve = resolve;
      });
    },

    resolveCardExpiryWarning(proceed) {
      this.cardExpiryWarningDialog = false;
      if (this._cardExpiryResolve) {
        this._cardExpiryResolve(proceed);
        this._cardExpiryResolve = null;
      }
    },

    hasScheduledPayments() {
      if (this.paymentMode !== "schedule") return false;
      return this.schedules
        .filter((s) => +s.amount > 0)
        .some((s) => this.isScheduledPayment(s.date));
    },

    getSchedulePayments() {
      return this.schedules.filter(
        (s) => this.isScheduledPayment(s.date) && +s.amount > 0
      );
    },

    getScheduleConfirmationDetails() {
      const today = moment().startOf("day");
      return this.schedules
        .filter((s) => +s.amount > 0 && this.isScheduledPayment(s.date))
        .map((s) => ({
          date: moment(s.date).format("L"),
          amount: formatCurrency(s.amount),
          isToday: moment(s.date)
            .startOf("day")
            .isSame(today),
        }));
    },

    getScheduleTotal() {
      return this.schedules
        .filter((s) => +s.amount > 0 && this.isScheduledPayment(s.date))
        .reduce((sum, s) => sum + +s.amount, 0);
    },

    shouldSkipAuthorization() {
      // If this submission includes both an immediate payment and a schedule,
      // always force dontAuthorize for the schedule request.
      return !!this.getCurrentAmount() && this.hasScheduledPayments();
    },

    getConfirmTotalAmount() {
      const currentAmount = +this.getCurrentAmount() || 0;
      if (!this.hasScheduledPayments()) return currentAmount;
      return currentAmount + this.getScheduleTotal();
    },

    hasAnyProcessingError() {
      return (
        this.paymentStatus === "D" ||
        this.paymentStatus === "E" ||
        !!this.scheduleError
      );
    },

    isScheduleOnlyResult() {
      return (
        this.scheduleProcessed &&
        !this.paymentProcessed &&
        !+this.getCurrentAmount()
      );
    },

    getPaymentMethodId() {
      const selected = this.paymentMethods.find(
        (p) => p.cardInfo === this.selectedCard
      );
      return selected ? selected.id : null;
    },

    getDirectPaymentInput() {
      if (this.hasAccountLoaded) return null;

      if (this.cardType === "Credit Card") {
        return {
          type: this.cardType,
          cardNumber: this.getInlineCardNumber(),
          expiration: this.expirationMonth + this.expirationYear,
          cvv: this.securityCode,
          nameOnAccount: "",
          routingNumber: "",
          accountNumber: "",
        };
      }

      return {
        type: this.cardType,
        cardNumber: "",
        expiration: "",
        cvv: "",
        nameOnAccount: this.nameOnAccount,
        routingNumber: this.routingNumber,
        accountNumber: this.accountNumber,
      };
    },

    getCategoryCode() {
      return this.allowAdminFeatures ? "DDIN" : "OTHER";
    },

    getPaymentDetails() {
      const amt = +this.getCurrentAmount();
      if (amt <= 0) return [];
      return [{ categoryCode: this.getCategoryCode(), amount: amt }];
    },

    getScheduleDetails() {
      const categoryCode = this.getCategoryCode();
      return this.schedules
        .filter((s) => this.isScheduledPayment(s.date) && +s.amount > 0)
        .map((detail) => ({
          date: moment(detail.date).format("YYYY-MM-DD HH:mm:ss"),
          amount: +detail.amount,
          details: [{ categoryCode: categoryCode, amount: +detail.amount }],
        }));
    },

    getServerNote() {
      return (
        "Department: " +
        this.getDepartmentName() +
        ", Note: " +
        (this.note || "")
      );
    },

    // ── Process ────────────────────────────────────────────

    async process() {
      this.processing = true;

      const basePayload = {
        user: this.$store.state.authentication.user,
        isTest: window.location.origin.startsWith("http://localhost"),
        accountID: this.selectedAccountId || this.selectedPersonId || null,
        name: this.name,
        address: this.address,
        phoneNumber: this.phoneNumber,
        emailAddress: this.emailAddress,
        note: this.getServerNote(),
        departmentID: this.selectedDepartmentId || null,
        paymentMethodID: this.hasAccountLoaded
          ? this.getPaymentMethodId()
          : null,
        directPayment: this.getDirectPaymentInput(),
      };

      if (this.getCurrentAmount()) {
        try {
          this.processingMessage = "Processing Payment...";
          await this.processPayment(basePayload);
          if (this.hasScheduledPayments()) {
            this.processingMessage = "Processing Schedule...";
            await this.processSchedule(basePayload);
          }
        } catch (e) {
          this.paymentProcessed = true;
          this.scheduleProcessed = true;
        } finally {
          this.processing = false;
          this.processingMessage = "";
        }
      } else {
        try {
          this.processingMessage = "Processing Schedule...";
          await this.processSchedule(basePayload);
        } catch (e) {
          this.paymentProcessed = true;
          this.scheduleProcessed = true;
        } finally {
          this.processing = false;
          this.processingMessage = "";
        }
      }
    },

    async processPayment(basePayload) {
      try {
        const response = await this.$http.post("api/payments/newPayment", {
          ...basePayload,
          amount: this.getCurrentAmount(),
          details: this.getPaymentDetails(),
        });
        this.paymentStatus = response.resultCode;
        this.paymentResult = response.transactionResult;
      } catch (e) {
        this.paymentStatus = "E";
        this.paymentResult = "An error has occurred";
        throw new Error("Payment Error");
      } finally {
        this.paymentProcessed = true;
      }
    },

    async processSchedule(basePayload) {
      this.scheduleError = "";
      try {
        await this.$http.post("api/payments/newSchedule", {
          ...basePayload,
          dontAuthorize: this.shouldSkipAuthorization() || this.dontAuthorize,
          dontValidateExpiration: true,
          scheduleDetails: this.getScheduleDetails(),
        });
      } catch (e) {
        this.scheduleError =
          (e && (e.message || e.Message)) || "An error occurred";
        throw new Error("Schedule Error");
      } finally {
        this.scheduleProcessed = true;
      }
    },

    closeNewPaymentMethodDialog() {
      this.newPaymentMethodDialog = false;
    },

    updateSelectedPaymentMethod(paymentMethod) {
      paymentMethod.cardInfo =
        paymentMethod.cardType + " - " + paymentMethod.cardNumberLast5;
      this.paymentMethods.push(paymentMethod);
      this.selectedCard = paymentMethod.cardInfo;
    },

    clearAccount() {
      this.selectedAccountId = null;
      this.selectedPersonId = null;
      this.selectedPersonLoaded = false;
      this.loadingAccountDetails = false;
      this.paymentMethods = [];
      this.selectedCard = "";
    },

    // ── Account Lookup ─────────────────────────────────────

    debouncedLookupSearch() {
      if (this.lookupDebounceTimer) clearTimeout(this.lookupDebounceTimer);
      this.lookupDebounceTimer = setTimeout(
        () => this.performLookupSearch(),
        400
      );
    },

    async performLookupSearch() {
      this.lookupResults = [];
      this.showNoLookupResults = false;
      const phone = (this.lookupPhone || "").trim();
      if (!phone || phone.length < 5) return;

      this.lookupLoading = true;
      try {
        const result = await this.$http.get(
          "api/donors/GetAccountByPhoneNumber?phoneNumber=" +
            encodeURIComponent(phone)
        );
        const arr = Array.isArray(result) ? result : result ? [result] : [];
        this.lookupResults = arr;
        this.showNoLookupResults = arr.length === 0;
      } catch (e) {
        this.lookupResults = [];
        this.showNoLookupResults = true;
      } finally {
        this.lookupLoading = false;
      }
    },

    getPersonDisplayName(person) {
      if (person.name) return person.name;
      const first = person.firstEnglish || person.firstName || "";
      const last = person.lastEnglish || person.lastName || "";
      return last && first ? last + ", " + first : last || first;
    },

    getPersonInitial(person) {
      const last = person.lastEnglish || person.lastName || "";
      const first = person.firstEnglish || person.firstName || "";
      return (last || first || "?").charAt(0).toUpperCase();
    },

    buildPersonAddress(person) {
      return [person.address1, person.city, person.state, person.zip]
        .filter(Boolean)
        .join(", ");
    },

    async selectPerson(person) {
      // Pre-fill immediately from the search result and close dialog right away
      this.name = this.getPersonDisplayName(person);
      this.address = this.buildPersonAddress(person);
      this.emailAddress = person.emailAddress || "";
      this.phoneNumber =
        person.homePhone || person.cellPhone || person.phone || "";
      this.selectedAccountId = person.parentId || null;
      this.selectedPersonId = person.personID || null;
      this.selectedPersonLoaded = true;
      this.closeAccountLookup();

      // Background-load full account details and saved cards if we have an account ID
      if (this.selectedAccountId) {
        this.loadingAccountDetails = true;
        try {
          const fullAccount = await this.$http.get(
            "api/accounts/GetAccount?accountID=" +
              encodeURIComponent(this.selectedAccountId)
          );
          if (fullAccount) {
            this.name = fullAccount.name || this.name;
            if (fullAccount.emailAddress)
              this.emailAddress = fullAccount.emailAddress;
            if (fullAccount.address1) this.address = fullAccount.address1;
            this.phoneNumber =
              fullAccount.homePhone ||
              fullAccount.cellPhone ||
              this.phoneNumber;
          }

          const cards = await this.$http.get(
            "api/payments/getSavedCards?accountID=" +
              encodeURIComponent(this.selectedAccountId)
          );
          this.paymentMethods = (cards || [])
            .map((m) => {
              m.cardInfo = m.cardType + " - " + m.cardNumberLast5;
              return m;
            })
            .sort((a, b) => {
              if (Date.parse(a.lastUsed) > Date.parse(b.lastUsed)) return -1;
              if (Date.parse(a.lastUsed) < Date.parse(b.lastUsed)) return 1;
              return 0;
            });

          if (this.paymentMethods.length) {
            this.selectedCard = this.paymentMethods[0].cardInfo;
          }
        } catch (e) {
          console.error("Failed to load account details", e);
        } finally {
          this.loadingAccountDetails = false;
        }
      }
    },

    closeAccountLookup() {
      this.accountLookupDialog = false;
    },

    reset() {
      if (this.lookupDebounceTimer) {
        clearTimeout(this.lookupDebounceTimer);
        this.lookupDebounceTimer = null;
      }
      this.accountLookupDialog = false;
      this.lookupPhone = "";
      this.lookupResults = [];
      this.lookupLoading = false;
      this.showNoLookupResults = false;

      this.name = "";
      this.phoneNumber = "";
      this.emailAddress = "";
      this.address = "";
      this.note = "";
      this.amount = 0;
      this.selectedDepartmentId = null;
      this.selectedCard = "";
      this.paymentMethods = [];
      this.selectedAccountId = null;
      this.selectedPersonId = null;
      this.selectedPersonLoaded = false;
      this.cardNumber = "";
      this.expirationMonth = "";
      this.expirationYear = "";
      this.securityCode = "";
      this.nameOnAccount = "";
      this.routingNumber = "";
      this.accountNumber = "";
      this.confirmAccountNumber = "";
      this.schedules = [];
      this.numberOfPayments = "1";
      this.scheduleEvery = "1";
      this.scheduleMode = "month";
      this.paymentMode = "single";
      this.paymentStatus = "";
      this.paymentResult = "";
      this.paymentProcessed = false;
      this.scheduleProcessed = false;
      this.scheduleError = "";
      this.showConfirmView = false;
      this.processingMessage = "";
    },
  },
};
</script>

<style>
.payment-type-label {
  font-size: 11px;
  font-weight: 700;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ── Mode toggles (native buttons for reliable active styling) ── */
.mode-toggle-group {
  display: flex;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
}
.mode-toggle-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 500;
  background: #fff;
  color: #555;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  outline: none;
  gap: 4px;
}
.mode-toggle-btn + .mode-toggle-btn {
  border-left: 1px solid #c8e6c9;
}
.mode-toggle-btn:hover {
  background: #f1f8e9;
  color: #2e7d32;
}
.mode-toggle-btn--active {
  background: #4caf50 !important;
  color: #fff !important;
}
.mode-toggle-btn--active .v-icon {
  color: #fff !important;
}

/* ── Mini toggles (interval, calendar) ── */
.mini-toggle-group {
  display: inline-flex;
  border: 1px solid #c8e6c9;
  border-radius: 4px;
  overflow: hidden;
}
.mini-toggle-btn {
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  background: #fff;
  color: #555;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  outline: none;
}
.mini-toggle-btn + .mini-toggle-btn {
  border-left: 1px solid #c8e6c9;
}
.mini-toggle-btn:hover {
  background: #f1f8e9;
  color: #2e7d32;
}
.mini-toggle-btn--active {
  background: #4caf50 !important;
  color: #fff !important;
}

/* ── Schedule config area ── */
.schedule-config {
  border-top: 1px solid #e0e0e0;
  padding-top: 12px;
}

/* ── Scrollable schedule rows ── */
.schedule-rows-container {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
  padding: 0 8px;
}
.schedule-row-item {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 6px 0 4px;
  border-bottom: 1px solid #eeeeee;
}
.schedule-row-item:last-child {
  border-bottom: none;
}
.schedule-row-num {
  font-size: 11px;
  color: #9e9e9e;
  min-width: 22px;
  text-align: right;
  padding-bottom: 6px;
}
.schedule-row-hebrew {
  font-size: 11px;
  color: #757575;
  min-width: 60px;
  padding-bottom: 6px;
}
.lookup-banner {
  display: flex;
  align-items: center;
  background: #f1f8e9;
  border: 1px solid #a5d6a7;
  border-radius: 6px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
}
.lookup-banner:hover {
  background: #e8f5e9;
}

.account-loaded-chip {
  display: flex;
  align-items: center;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 6px;
  padding: 8px 14px;
}

.sticky-actions {
  position: sticky;
  bottom: 0;
  z-index: 4;
  background: white;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 0 12px;
  margin-top: 8px;
}
.result-actions {
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}
.history-tabs-bar {
  display: flex;
  align-items: center;
  background: #4caf50;
  border-radius: 4px 4px 0 0;
  padding-right: 4px;
}
.history-tabs {
  flex: 1;
}
.history-tabs .v-tabs__bar {
  background: transparent !important;
}
.history-tab-item {
  text-transform: none;
  min-width: 0;
  font-size: 13px;
  letter-spacing: 0.3px;
  color: rgba(255, 255, 255, 0.5) !important;
  font-weight: 600;
}
.history-tab-item .v-icon {
  color: rgba(255, 255, 255, 0.5) !important;
}
.history-tab-item.v-tab--active {
  color: #fff !important;
  font-weight: 800 !important;
}
.history-tab-item.v-tab--active .v-icon {
  color: #fff !important;
}
.history-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}
.v-tab--active .history-count-badge {
  background: rgba(255, 255, 255, 0.9);
  color: #2e7d32;
}
.history-refresh-btn {
  flex-shrink: 0;
  opacity: 0.8;
}
.history-refresh-btn:hover {
  opacity: 1;
}
.history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 0;
  color: #9e9e9e;
}
.history-table-wrap {
  overflow-x: auto;
  margin-top: 8px;
}
.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.history-table th {
  text-align: left;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}
.history-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  color: #333;
}
.history-table tbody tr:hover {
  background: #f9fbe7;
}
.history-date {
  white-space: nowrap;
  color: #616161;
  font-size: 12px;
}
.history-amount {
  font-weight: 600;
  color: #2e7d32;
  white-space: nowrap;
}
.history-card {
  font-size: 12px;
  white-space: nowrap;
}
.history-status-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  border-radius: 8px;
  padding: 2px 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.history-status--processed {
  background: #e8f5e9;
  color: #2e7d32;
}
.history-status--declined {
  background: #fce4ec;
  color: #c62828;
}
.history-status--error {
  background: #fff3e0;
  color: #e65100;
}
.history-status--voided {
  background: #eceff1;
  color: #546e7a;
}
.history-status--active {
  background: #e3f2fd;
  color: #1565c0;
}
.history-status--scheduled {
  background: #e3f2fd;
  color: #1565c0;
}
.history-status--completed {
  background: #e8f5e9;
  color: #2e7d32;
}
.history-status--cancelled {
  background: #eceff1;
  color: #546e7a;
}
.history-status--unknown {
  background: #f5f5f5;
  color: #9e9e9e;
}
.history-sched-pills {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.sched-pill {
  font-size: 11px;
  font-weight: 600;
  border-radius: 8px;
  padding: 1px 7px;
}
.sched-pill--ok {
  background: #e8f5e9;
  color: #2e7d32;
}
.sched-pill--pending {
  background: #fff8e1;
  color: #f57f17;
}
.sched-pill--bad {
  background: #fce4ec;
  color: #c62828;
}
.sections-row {
  margin: 0 -8px;
}
.section-col {
  padding: 0 8px;
  margin-bottom: 16px;
}
.cp-section {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 14px 16px 16px;
}
.section-label {
  font-size: 12px;
  font-weight: 700;
  color: #4caf50;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.confirm-heading {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
  color: #f57c00;
}
.confirm-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
}
.confirm-row:last-child {
  border-bottom: none;
}
.confirm-key {
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 72px;
  flex-shrink: 0;
}
.confirm-val {
  font-size: 14px;
  color: #333;
  word-break: break-word;
}
.confirm-val--amount {
  font-size: 16px;
  font-weight: 600;
  color: #2e7d32;
}
.confirm-val--badge {
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 10px;
  padding: 1px 10px;
  font-size: 12px;
  font-weight: 600;
}
.confirm-schedule-grid {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
}
.confirm-schedule-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.confirm-schedule-item:last-child {
  border-bottom: none;
}
.confirm-schedule-date {
  flex: 1;
  font-size: 13px;
  color: #444;
}
.confirm-schedule-amount {
  font-size: 13px;
  font-weight: 600;
  color: #2e7d32;
  min-width: 64px;
  text-align: right;
}
.confirm-today-badge {
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #1976d2;
  border-radius: 8px;
  padding: 1px 7px;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}
.confirm-schedule-item--today {
  background: #e3f2fd;
}
.confirm-grand-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 12px;
  background: #f1f8e9;
  border: 1px solid #c8e6c9;
  border-radius: 0 0 6px 6px;
  font-size: 13px;
  font-weight: 700;
  color: #2e7d32;
}
.lookup-result-tile:hover {
  background-color: #f1f8e9 !important;
  cursor: pointer;
}
.lookup-detail {
  font-size: 12px;
}
.person-list-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  margin-top: 12px;
}
.person-list {
  background: transparent;
  padding: 0;
}
.person-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
  padding: 8px 10px;
  min-height: auto;
}
.person-item:hover {
  background-color: #e8f5e9 !important;
}
.person-item:last-child {
  border-bottom: none;
}
.person-name {
  line-height: 1.6;
  margin-bottom: 6px;
  word-wrap: break-word;
}
.person-details {
  line-height: 1.6;
}
.detail-line {
  display: flex;
  align-items: flex-start;
  margin-bottom: 2px;
}
.detail-line:last-child {
  margin-bottom: 0;
}
.detail-icon {
  margin-right: 6px;
  margin-top: 2px;
  flex-shrink: 0;
}
.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

/* ── Card Expiry Warning Dialog ── */
.card-expiry-warn-card {
  border-radius: 12px !important;
  overflow: hidden;
}
.card-expiry-warn-header {
  display: flex;
  align-items: center;
  padding: 20px 20px 12px;
  background: #fff8e1;
  border-bottom: 1px solid #ffe082;
}
.card-expiry-warn-title {
  font-size: 16px;
  font-weight: 700;
  color: #e65100;
}
.card-expiry-warn-body {
  padding: 20px 20px 8px !important;
}
.card-expiry-warn-message {
  font-size: 14px;
  color: #424242;
  line-height: 1.5;
}
.card-expiry-warn-note {
  font-size: 12px;
  color: #757575;
  display: flex;
  align-items: flex-start;
  line-height: 1.5;
}
.card-expiry-warn-actions {
  padding: 8px 16px 16px !important;
}
</style>
